import { PUBLIC_DEFAULT_LOCALE } from "./localization";

export const SITE_ORIGIN = "https://on45d.studio";
export const GA_MEASUREMENT_ID = "G-MG6VWRQ2ZC";
export const DEFAULT_TITLE = "ON45D Studio";
export const DEFAULT_DESCRIPTION =
  "ON45D es un estudio en Tampa para marcas que necesitan un sitio, una identidad o un vehiculo que aguante el uso real.";
export const STUDIO_EMAIL = "hello@on45d.studio";
export const STUDIO_PHONE = "+18137120063";
export const STUDIO_SAME_AS = [
  "https://www.facebook.com/on45d",
  "https://www.instagram.com/on45d",
  "https://www.linkedin.com/company/on45d",
];
export const STUDIO_SERVICES = [
  { name: "Websites and apps", description: "Sites and products that hold up in daily use." },
  { name: "Identity and print", description: "Marks, decks, and collateral." },
  { name: "Vehicle wraps", description: "Fleet and car graphics." },
];

/** Public URLs are always https, except on a local dev server. */
function httpsOrigin(origin: string): string {
  return /^http:\/\/(localhost|127\.0\.0\.1)(:|$)/.test(origin) ? origin : origin.replace(/^http:/, "https:");
}

export function siteOrigin(baseUrl?: string): string {
  const candidate = String(baseUrl || "").trim();
  if (candidate) {
    try {
      return httpsOrigin(new URL(candidate).origin);
    } catch {
      // fall through
    }
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return httpsOrigin(window.location.origin);
  }
  return SITE_ORIGIN;
}

export function publicCanonicalPath(pathname: string): string {
  const path = pathname || "/";
  if (path === "/" || path === "/start") return "/";
  return path;
}

export function absoluteUrl(pathOrUrl: string, origin = SITE_ORIGIN): string {
  const value = String(pathOrUrl || "").trim();
  if (!value) return `${origin.replace(/\/$/, "")}/`;
  if (/^https?:\/\//i.test(value)) return value;
  const base = origin.replace(/\/$/, "");
  const path = value.startsWith("/") ? value : `/${value}`;
  return path === "/" ? `${base}/` : `${base}${path}`;
}

/**
 * URL of a page in one language. The default language (English) is the clean URL, so it matches
 * x-default and the canonical; other languages add ?lang=.
 */
export function hreflangUrl(origin: string, pathname: string, locale: string): string {
  const url = new URL(publicCanonicalPath(pathname), `${origin.replace(/\/$/, "")}/`);
  if (locale !== PUBLIC_DEFAULT_LOCALE) url.searchParams.set("lang", locale);
  return url.toString();
}

/**
 * Canonical URL of a public page: the page itself in the language shown. A canonical URL set in
 * the website editor applies to the homepage only; on other pages it would tell Google that every
 * page is a copy of the homepage.
 */
export function pageCanonicalUrl(
  origin: string,
  pathname: string,
  locale: string,
  homepageOverride?: string
): string {
  const override = String(homepageOverride || "").trim();
  if (override && publicCanonicalPath(pathname) === "/" && locale === PUBLIC_DEFAULT_LOCALE) {
    return absoluteUrl(override.replace(/^http:/, "https:").replace(/^(https:\/\/[^/]+)$/, "$1/"));
  }
  return hreflangUrl(origin, pathname, locale);
}

export function setMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setLinkRel(rel: string, href: string, attrs: Record<string, string> = {}) {
  const extra = Object.entries(attrs)
    .map(([key, value]) => `[${key}="${value}"]`)
    .join("");
  let el = document.querySelector(`link[rel="${rel}"]${extra}`);
  if (!el) {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value));
    document.head.appendChild(link);
    el = link;
  }
  el.setAttribute("href", href);
}

export function setJsonLd(data: Record<string, unknown>) {
  let script = document.querySelector('script[type="application/ld+json"][data-seo-schema="organization"]');
  if (!script) {
    script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-schema", "organization");
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

export function organizationSchema(options: {
  name?: string;
  url?: string;
  email?: string;
  logo?: string;
  type?: string;
} = {}) {
  const type = options.type || "ProfessionalService";
  const url = options.url || SITE_ORIGIN;
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": type,
    name: options.name || DEFAULT_TITLE,
    legalName: "ON45D Studio LLC",
    url,
    email: options.email || STUDIO_EMAIL,
    telephone: STUDIO_PHONE,
    sameAs: STUDIO_SAME_AS,
    areaServed: [
      { "@type": "City", name: "Tampa" },
      { "@type": "AdministrativeArea", name: "Florida" },
      { "@type": "Country", name: "United States" },
    ],
    knowsAbout: ["web design", "brand identity", "vehicle wraps", "graphic design"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "6402 Crystal Brook Drive",
      addressLocality: "Tampa",
      addressRegion: "FL",
      postalCode: "33625",
      addressCountry: "US",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "ON45D Studio services",
      itemListElement: STUDIO_SERVICES.map((service) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: service.name,
          description: service.description,
        },
      })),
    },
  };
  const logo = options.logo || absoluteUrl("/icons/icon-512.png", url);
  data.logo = logo;
  data.image = logo;
  return data;
}

export function applyRobots(index: boolean, follow: boolean) {
  setMetaByName("robots", `${index ? "index" : "noindex"}, ${follow ? "follow" : "nofollow"}`);
}
